'use client';

import {
  makeAutoObservable,
  runInAction,
} from 'mobx';

import { AssistantApprovalStore } from './assistantApprovalStore';
import { AssistantConversationStore } from './assistantConversationStore';
import { AssistantModelStore } from './assistantModelStore';
import {
  computeGatewayReadiness,
  hasUnsatisfiedCliRequirement,
  mergeCursorCliRequirement,
} from './assistantReadiness';
import {
  AssistantTabsStore,
  isDraftTabId,
} from './assistantTabsStore';
import { normaliseAssistantTranscriptMessages } from './assistantTranscriptUtils';


import type { GatewayReadinessRequirement } from './assistantReadiness';
import type {
  GatewayAgentInfo,
  GatewayCredentialStatus,
  GatewayErrorKind,
  GatewayEvent,
  GatewayModelSelection,
  GatewaySettings,
  GatewayStatus,
} from '@/app/sdk';

import { PRODUCT_NAME } from '@/const';

export type { AssistantAgentStats, AssistantContextUsageEstimate, AssistantUsage } from './assistantTokenUsage';
export type { ConversationKey, ConversationRuntime } from './assistantConversationStore';
export type {
  AssistantMcpApproval,
  AssistantMessage,
  AssistantMessageBlock,
  AssistantMessageRole,
  AssistantAgentTab,
  AssistantRunStatusEvent,
  AssistantStepEvent,
  AssistantSummaryEvent,
  AssistantToolCall,
  AssistantUiState,
} from './assistantTypes';

export { ASSISTANT_NEW_AGENT_LABEL, isDraftTabId } from './assistantTabsStore';
export const ASSISTANT_CURRENT_AGENT_LABEL = 'Current';

export type AssistantSettingsTab = 'connection' | 'models' | 'mcp';

type AssistantGatewayBackendId = 'cursor-cli-acp' | 'cursor-sdk';
type AssistantGatewayBackendMode = 'automatic' | AssistantGatewayBackendId;
type AssistantCursorCliStatus = {
  available: boolean;
  commandPath?: string;
  version?: string;
  supportsAcp: boolean;
  installable: boolean;
  problemCode?: string;
  message?: string;
};

const VERBOSE_ASSISTANT_LOG = false && process.env.NODE_ENV === 'development';

function verboseLog(message: string, meta?: Record<string, unknown>): void {
  if (!VERBOSE_ASSISTANT_LOG) {
    return;
  }
  console.debug(`[assistant] ${message}`, meta ?? {});
}

function normaliseGatewayMode(mode: string): AssistantGatewayBackendMode {
  if (mode === 'cursor-sdk') {
    return 'cursor-sdk';
  }
  if (mode === 'cursor-cli-acp') {
    return 'cursor-cli-acp';
  }
  return 'automatic';
}

function normaliseGatewayBackendId(value: string | null | undefined): AssistantGatewayBackendId | null {
  if (value === 'cursor-sdk' || value === 'cursor-cli-acp') {
    return value;
  }
  return null;
}

function cloneGatewayModelSelection(selection: GatewayModelSelection): GatewayModelSelection {
  const params = selection.params?.map(param => ({ id: param.id, value: param.value }));
  return {
    id: selection.id,
    params: params && params.length > 0 ? params : undefined,
  };
}

function normaliseAgentTimestamp(value: number, fallback = 0): number {
  if (!Number.isFinite(value) || value <= 0) {
    return fallback;
  }
  return value;
}

/**
 * MobX store for managing the Genesys AI Assistant state and logic.
 *
 * Orchestrates gateway lifecycle, agent CRUD, and gateway event routing.
 * Domain state lives in sub-stores: conversations, tabs, model, approvals.
 */
export class AssistantStore {
  readonly conversations: AssistantConversationStore;
  readonly tabs: AssistantTabsStore;
  readonly model: AssistantModelStore;
  readonly approvals: AssistantApprovalStore;

  statusLabel = `${PRODUCT_NAME} AI • Setup Required`;
  errorMessage: string | null = null;
  isReady = false;
  configured = false;
  gatewayMode: AssistantGatewayBackendMode = 'automatic';
  activeBackendId: AssistantGatewayBackendId | null = null;
  cursorCliStatus: AssistantCursorCliStatus | null = null;
  private lastBackendRequirements: GatewayReadinessRequirement[] | undefined = undefined;
  needsCredential = false;
  hasDeclinedOnboarding = false;
  credentialFromEnvironment = false;
  credentialMaskedKey: string | undefined = undefined;
  showOnboardingDialog = false;
  showSettingsDialog = false;
  settingsInitialTab: AssistantSettingsTab | null = null;
  activeAgentId: string | null = null;
  agents: GatewayAgentInfo[] = [];
  isLoadingAgents = false;
  isRecoveringGateway = false;
  lastErrorKind: GatewayErrorKind | null = null;
  isAgentTransitioning = false;
  agentTransitionTargetAgentId: string | null = null;

  lastProjectPath: string | null = null;
  private unsubscribe: (() => void) | null = null;
  private metadataSyncPromise: Promise<void> | null = null;
  private metadataSyncProjectPath: string | null = null;
  private hasQueuedAgentRefresh = false;
  private agentTransitionToken = 0;

  constructor() {
    this.conversations = new AssistantConversationStore(this);
    this.model = new AssistantModelStore(this);
    this.approvals = new AssistantApprovalStore(this);
    this.tabs = new AssistantTabsStore(this);

    makeAutoObservable(this, {
      conversations: false,
      tabs: false,
      model: false,
      approvals: false,
    }, { autoBind: true });
  }

  get isAgentActionsDisabled(): boolean {
    return this.isAgentTransitioning;
  }

  async start(projectPath: string): Promise<void> {
    const gateway = typeof window !== 'undefined' ? window.electronAPI?.gateway : null;
    if (this.lastProjectPath && this.lastProjectPath !== projectPath) {
      this.conversations.clearAllConversations();
      this.tabs.clearTabPaneKeysOnProjectChange();
      this.metadataSyncPromise = null;
      this.metadataSyncProjectPath = null;
    }
    this.lastProjectPath = projectPath;

    if (!gateway) {
      runInAction(() => {
        this.errorMessage = 'Gateway is only available in the SDK Electron editor.';
        const activeKey = this.conversations.resolveActiveConversationKey();
        if (activeKey) {
          const rt = this.conversations.getOrCreateConversation(activeKey, this.activeAgentId);
          rt.assistantState = 'error';
        }
        this.statusLabel = `${PRODUCT_NAME} AI • Setup Required`;
      });
      return;
    }

    this.disposeSubscription();
    this.unsubscribe = gateway.onEvent((event) => this.handleGatewayEvent(event));

    this.tabs.loadTabLayoutFromStorage(projectPath);

    try {
      await this.syncFromGateway();
      runInAction(() => {
        if (this.isReady && this.lastErrorKind !== 'run') {
          this.errorMessage = null;
          this.lastErrorKind = null;
        }
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      runInAction(() => {
        this.errorMessage = message;
        const activeKey = this.conversations.resolveActiveConversationKey();
        if (activeKey) {
          const rt = this.conversations.getOrCreateConversation(activeKey, this.activeAgentId);
          rt.assistantState = 'error';
        }
        this.lastErrorKind = 'startup';
      });
    }
  }

  async onCredentialSaved(): Promise<void> {
    runInAction(() => {
      this.showOnboardingDialog = false;
      this.errorMessage = null;
      this.lastErrorKind = null;
    });

    const gateway = window.electronAPI?.gateway;
    if (gateway && this.lastProjectPath) {
      try {
        await gateway.restart(this.lastProjectPath);
      } catch {
        // restart is best-effort; sync below reflects actual gateway state
      }
    }

    await this.syncFromGateway();
  }

  async syncFromGateway(): Promise<void> {
    await this.syncMetadataFromGateway();
    await this.conversations.reattachActiveRunSnapshots();
    const activeKey = this.conversations.resolveActiveConversationKey();
    const activeRt = activeKey ? this.conversations.conversationsByKey.get(activeKey) : undefined;
    if (!activeRt?.activeRunId && !activeRt?.hasPendingRun && !activeRt?.hydrated) {
      await this.conversations.hydrateAgentTranscript(this.activeAgentId);
    }
  }

  async syncMetadataFromGateway(): Promise<void> {
    const syncProjectPath = this.lastProjectPath;
    if (this.metadataSyncPromise && this.metadataSyncProjectPath === syncProjectPath) {
      await this.metadataSyncPromise;
      return;
    }

    const metadataSyncPromise = this.syncMetadataFromGatewayInternal(syncProjectPath);
    this.metadataSyncPromise = metadataSyncPromise;
    this.metadataSyncProjectPath = syncProjectPath;
    try {
      await metadataSyncPromise;
    } finally {
      if (this.metadataSyncPromise === metadataSyncPromise) {
        this.metadataSyncPromise = null;
        this.metadataSyncProjectPath = null;
      }
    }
  }

  async recoverGateway(): Promise<void> {
    const gateway = window.electronAPI?.gateway;
    if (!gateway || !this.lastProjectPath || this.isRecoveringGateway) {
      return;
    }

    runInAction(() => {
      this.isRecoveringGateway = true;
    });
    console.info('[gateway] Recovering assistant gateway');

    try {
      await gateway.restart(this.lastProjectPath);
      await this.syncFromGateway();
      runInAction(() => {
        if (this.isReady && this.lastErrorKind !== 'run') {
          this.errorMessage = null;
          this.lastErrorKind = null;
          const activeKey = this.conversations.resolveActiveConversationKey();
          const rt = activeKey ? this.conversations.conversationsByKey.get(activeKey) : undefined;
          if (rt?.assistantState === 'error') {
            rt.assistantState = 'idle';
          }
        }
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      runInAction(() => {
        this.errorMessage = message;
        this.lastErrorKind = 'startup';
        const activeKey = this.conversations.resolveActiveConversationKey();
        const rt = activeKey ? this.conversations.conversationsByKey.get(activeKey) : undefined;
        if (rt) rt.assistantState = 'error';
      });
      console.error('[gateway] Failed to recover assistant gateway', { message });
    } finally {
      runInAction(() => {
        this.isRecoveringGateway = false;
      });
    }
  }

  async skipOnboarding(): Promise<void> {
    const gateway = window.electronAPI?.gateway;
    try {
      await gateway?.declineCredentialOnboarding();
    } catch {
      // ignore decline failures
    }
    runInAction(() => {
      this.showOnboardingDialog = false;
      this.hasDeclinedOnboarding = true;
    });
    await this.syncFromGateway();
  }

  openSettings(initialTab?: AssistantSettingsTab): void {
    this.settingsInitialTab = initialTab ?? null;
    this.showSettingsDialog = true;
    void this.syncMetadataFromGateway();
  }

  closeSettings(): void {
    this.showSettingsDialog = false;
    this.settingsInitialTab = null;
  }

  async refreshCursorCliStatus(): Promise<void> {
    const gateway = window.electronAPI?.gateway;
    if (!gateway?.getCursorCliStatus) {
      return;
    }
    try {
      const status = await gateway.getCursorCliStatus();
      runInAction(() => {
        this.cursorCliStatus = status;
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      runInAction(() => {
        this.errorMessage = message;
      });
    }
  }

  async installCursorCli(): Promise<{ success: boolean; message: string; installUrl?: string }> {
    const gateway = window.electronAPI?.gateway;
    if (!gateway?.installCursorCli) {
      return { success: false, message: 'Gateway CLI install is unavailable.' };
    }
    try {
      const result = await gateway.installCursorCli();
      runInAction(() => {
        this.cursorCliStatus = result.status;
      });
      if (result.success && result.status.available) {
        void this.model.loadAvailableModels();
      }
      return {
        success: result.success,
        message: result.message,
        installUrl: 'installUrl' in result ? result.installUrl : undefined,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      runInAction(() => {
        this.errorMessage = message;
      });
      return { success: false, message };
    }
  }

  async updateCursorCli(): Promise<{ success: boolean; message: string; installUrl?: string }> {
    const gateway = window.electronAPI?.gateway;
    if (!gateway?.updateCursorCli) {
      return { success: false, message: 'Gateway CLI update is unavailable.' };
    }
    try {
      const result = await gateway.updateCursorCli();
      runInAction(() => {
        this.cursorCliStatus = result.status;
      });
      if (result.success && result.status.available) {
        void this.model.loadAvailableModels();
      }
      return {
        success: result.success,
        message: result.message,
        installUrl: 'installUrl' in result ? result.installUrl : undefined,
      };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      runInAction(() => {
        this.errorMessage = message;
      });
      return { success: false, message };
    }
  }

  async setCursorCliPath(
    cliPath: string | null,
    projectPath?: string,
  ): Promise<{ success: boolean; warningMessage?: string; message?: string }> {
    const gateway = window.electronAPI?.gateway;
    if (!gateway?.setCursorCliPath || !gateway.restart) {
      return { success: false, message: 'Gateway CLI path update is unavailable.' };
    }
    try {
      await gateway.setCursorCliPath(cliPath);
      await this.refreshCursorCliStatus();
      await gateway.restart(projectPath);
      const status = this.cursorCliStatus;
      if (!status?.available) {
        return {
          success: true,
          warningMessage: status?.message ?? 'Configured CLI path did not pass Cursor CLI checks.',
        };
      }
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      runInAction(() => {
        this.errorMessage = message;
      });
      return { success: false, message };
    }
  }

  openOnboarding(): void {
    this.showOnboardingDialog = true;
  }

  closeOnboarding(): void {
    this.showOnboardingDialog = false;
  }

  async refreshAgents(): Promise<void> {
    const gateway = window.electronAPI?.gateway;
    if (!gateway || !this.lastProjectPath) return;
    if (this.isLoadingAgents) {
      this.hasQueuedAgentRefresh = true;
      return;
    }
    runInAction(() => {
      this.isLoadingAgents = true;
      this.hasQueuedAgentRefresh = false;
    });

    try {
      const agents = await gateway.listAgents(this.lastProjectPath);
      runInAction(() => {
        const normalisedAgents = this.normaliseAgents(agents);
        this.agents = normalisedAgents;
        this.conversations.syncRuntimeStatesFromAgents(normalisedAgents);
        this.tabs.reconcileAgentTabs();
        this.tabs.persistTabLayout();
      });
    } finally {
      runInAction(() => {
        this.isLoadingAgents = false;
      });
      if (this.hasQueuedAgentRefresh) {
        this.hasQueuedAgentRefresh = false;
        void this.refreshAgents();
      }
    }
  }

  async startNewAgent(): Promise<void> {
    const gateway = window.electronAPI?.gateway;
    if (!gateway || this.isAgentTransitioning) {
      return;
    }

    this.tabs.flushActiveDraftPrompt();

    const newDraftTabId = this.tabs.appendNewDraftTab();
    runInAction(() => {
      this.activeAgentId = null;
      this.tabs.persistTabLayout();
    });

    const transitionToken = this.beginAgentTransition(null);
    try {
      if (!this.isCurrentAgentTransition(transitionToken)) {
        return;
      }

      const settings = await gateway.startNewAgent(this.lastProjectPath ?? undefined);
      if (!this.isCurrentAgentTransition(transitionToken)) {
        return;
      }

      runInAction(() => {
        this.applySettings(settings);
        this.tabs.reconcileAgentTabs(undefined, newDraftTabId);
        this.conversations.getOrCreateConversation(newDraftTabId, null);
        this.approvals.clearApprovals();
      });

      await this.refreshAgents();
      runInAction(() => {
        this.tabs.persistTabLayout();
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      runInAction(() => {
        this.errorMessage = message;
        this.lastErrorKind = 'startup';
      });
    } finally {
      this.endAgentTransition(transitionToken);
    }
  }

  async switchAgent(agentId: string): Promise<void> {
    const gateway = window.electronAPI?.gateway;
    const trimmedAgentId = agentId.trim();
    if (!gateway || !trimmedAgentId || this.isAgentTransitioning) {
      return;
    }

    const currentTabAgentId = this.tabs.resolveAgentIdFromTabId(this.tabs.getActiveTabId());
    if (currentTabAgentId === trimmedAgentId && this.activeAgentId?.trim() === trimmedAgentId) {
      return;
    }

    const transitionToken = this.beginAgentTransition(trimmedAgentId);
    try {
      const settings = await gateway.switchAgent(trimmedAgentId, this.lastProjectPath ?? undefined);
      if (!this.isCurrentAgentTransition(transitionToken)) {
        return;
      }

      runInAction(() => {
        this.applySettings(settings);
        this.activeAgentId = trimmedAgentId;
        this.tabs.ensureAgentTabOpen(trimmedAgentId);
        this.tabs.setActiveTabIdForAgentSwitch(trimmedAgentId);
        this.conversations.getOrCreateConversation(trimmedAgentId, trimmedAgentId);
        this.approvals.clearApprovals();
        this.tabs.reconcileAgentTabs();
        this.tabs.persistTabLayout();
      });

      const rt = this.conversations.conversationsByKey.get(trimmedAgentId);
      if (!rt?.hydrated && !rt?.activeRunId && !rt?.hasPendingRun) {
        await this.conversations.hydrateAgentTranscript(trimmedAgentId);
      }
      await this.refreshAgents();
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      runInAction(() => {
        this.errorMessage = message;
        this.lastErrorKind = 'startup';
      });
    } finally {
      this.endAgentTransition(transitionToken);
    }
  }

  async renameAgent(agentId: string, displayName: string): Promise<boolean> {
    const gateway = window.electronAPI?.gateway;
    const trimmedAgentId = agentId.trim();
    const trimmedName = displayName.trim();
    if (!gateway || !trimmedAgentId || !trimmedName || this.isAgentTransitioning) {
      return false;
    }

    try {
      await gateway.renameAgent(trimmedAgentId, trimmedName);
      await this.refreshAgents();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      runInAction(() => {
        this.errorMessage = message;
      });
      return false;
    }
  }

  async deleteAgent(agentId: string): Promise<boolean> {
    const gateway = window.electronAPI?.gateway;
    const trimmedAgentId = agentId.trim();
    if (!gateway || !trimmedAgentId || this.isAgentTransitioning) {
      return false;
    }

    const deletedTabId = trimmedAgentId;
    const isDeletingActiveAgent = trimmedAgentId === this.activeAgentId;
    const fallbackTabId = isDeletingActiveAgent ? this.tabs.findNeighbourAgentTabId(deletedTabId) : null;

    try {
      const settings = await gateway.deleteAgent(trimmedAgentId);
      runInAction(() => {
        this.conversations.deleteConversation(trimmedAgentId);
        this.tabs.removeAgentTab(trimmedAgentId, fallbackTabId, isDeletingActiveAgent);
        this.applySettings(settings);
      });

      if (isDeletingActiveAgent) {
        runInAction(() => {
          this.approvals.clearApprovals();
        });
      }

      await this.refreshAgents();

      if (isDeletingActiveAgent) {
        await this.tabs.selectAgentTabAfterActiveDelete(fallbackTabId);
      }

      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      runInAction(() => {
        this.errorMessage = message;
      });
      return false;
    }
  }

  async sendPrompt(text: string): Promise<void> {
    const gateway = window.electronAPI?.gateway;
    const trimmed = text.trim();
    if (!gateway || !trimmed) return;

    if (!this.isReady) {
      runInAction(() => {
        this.errorMessage = this.needsCredential
          ? `Connect a Cursor API key in ${PRODUCT_NAME} AI Settings to use the assistant.`
          : 'Gateway is not ready yet. Please wait and try again.';
      });
      return;
    }

    if (this.needsCredential) {
      runInAction(() => {
        this.showOnboardingDialog = true;
      });
      return;
    }

    const activeTabIdBeforeSend = this.tabs.getActiveTabId();
    const targetAgentId = this.tabs.resolveAgentIdFromTabId(activeTabIdBeforeSend);
    const activeKey = activeTabIdBeforeSend;
    const isFirstMessage = this.conversations.messages.length === 0;

    runInAction(() => {
      this.errorMessage = null;
      this.lastErrorKind = null;
      const rt = this.conversations.getOrCreateConversation(activeKey, targetAgentId);
      const supersededRunId = rt.assistantState === 'running' ? rt.activeRunId : null;
      if (supersededRunId !== null) {
        rt.ignoredRunIds.add(supersededRunId);
        this.conversations.finaliseStreamingMessageForRuntime(rt);
      }
      rt.messages.push(this.conversations.createUserMessage(trimmed));
      rt.assistantState = 'running';
      rt.activeRunId = null;
      rt.hasPendingRun = true;
    });
    if (isFirstMessage && isDraftTabId(activeTabIdBeforeSend)) {
      this.tabs.setDraftTitleFromPrompt(activeTabIdBeforeSend, trimmed);
    }
    if (isDraftTabId(activeTabIdBeforeSend)) {
      runInAction(() => {
        this.tabs.deleteDraft(activeTabIdBeforeSend);
        this.tabs.persistTabLayout();
      });
    }
    if (isFirstMessage) {
      runInAction(() => {
        this.conversations.setPendingAutoNamePrompt(trimmed);
      });
      this.conversations.flushPendingAutoName();
    }

    try {
      const options: Parameters<typeof gateway.sendPrompt>[1] = {
        runMode: this.model.runMode,
        model: cloneGatewayModelSelection(this.model.resolveModelSelectionForGateway()),
        agentId: targetAgentId ?? undefined,
      };
      await gateway.sendPrompt(trimmed, options);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      runInAction(() => {
        const rt = this.conversations.conversationsByKey.get(activeKey);
        if (rt) {
          this.conversations.finaliseStreamingMessageForRuntime(rt);
          rt.assistantState = 'error';
          rt.activeRunId = null;
          rt.hasPendingRun = false;
        }
        this.errorMessage = message;
        this.lastErrorKind = 'startup';
      });
    }
  }

  async abort(): Promise<void> {
    const gateway = window.electronAPI?.gateway;
    if (!gateway) return;
    try {
      await gateway.abort(this.activeAgentId ?? undefined);
    } catch {
      // ignore abort failures
    }
    runInAction(() => {
      const activeKey = this.conversations.resolveActiveConversationKey();
      const rt = activeKey ? this.conversations.conversationsByKey.get(activeKey) : undefined;
      if (rt) {
        this.conversations.finaliseStreamingMessageForRuntime(rt);
        rt.assistantState = 'idle';
        rt.activeRunId = null;
        rt.hasPendingRun = false;
        rt.ignoredRunIds.clear();
      }
    });
  }

  detach(): void {
    this.approvals.disposeMcpApprovalTimers();
    this.disposeSubscription();
  }

  async stop(): Promise<void> {
    const gateway = window.electronAPI?.gateway;
    this.approvals.disposeMcpApprovalTimers();
    this.disposeSubscription();
    if (gateway) {
      try {
        await gateway.stop();
      } catch {
        // ignore stop failures
      }
    }
    runInAction(() => {
      this.isReady = false;
      this.lastProjectPath = null;
      this.metadataSyncPromise = null;
      this.metadataSyncProjectPath = null;
      this.conversations.clearAllConversations();
      this.tabs.resetTabsOnStop();
      this.activeAgentId = null;
      this.hasQueuedAgentRefresh = false;
      this.isAgentTransitioning = false;
      this.agentTransitionTargetAgentId = null;
    });
  }

  clearMessages(): void {
    this.conversations.clearMessages();
  }

  upsertLocalAgent(agentId: string, displayName: string): void {
    const now = Date.now();
    const existing = this.agents.find(a => a.agentId === agentId);
    if (existing) {
      this.agents = this.agents.map(a =>
        a.agentId === agentId ? { ...a, displayName } : a,
      );
    } else {
      this.agents = [
        {
          agentId,
          displayName,
          projectPath: this.lastProjectPath ?? '',
          createdAt: now,
          updatedAt: now,
        },
        ...this.agents,
      ];
    }
  }

  beginAgentTransition(targetAgentId: string | null): number {
    this.agentTransitionToken += 1;
    this.isAgentTransitioning = true;
    this.agentTransitionTargetAgentId = targetAgentId;
    return this.agentTransitionToken;
  }

  isCurrentAgentTransition(token: number): boolean {
    return this.isAgentTransitioning && this.agentTransitionToken === token;
  }

  endAgentTransition(token: number): void {
    if (this.agentTransitionToken !== token) {
      return;
    }
    this.isAgentTransitioning = false;
    this.agentTransitionTargetAgentId = null;
  }

  applySettings(settings: GatewaySettings): void {
    this.model.applyModelSettings(settings);
    this.approvals.applyMcpApprovalSettings(settings.mcpApproval);
    this.tabs.reconcileAgentTabs();
    this.tabs.persistTabLayout();
  }

  async updateGatewaySettings(settings: Partial<GatewaySettings>): Promise<void> {
    const gateway = window.electronAPI?.gateway;
    if (!gateway) return;

    try {
      const nextSettings = await gateway.setSettings(settings);
      runInAction(() => {
        this.applySettings(nextSettings);
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      runInAction(() => {
        this.errorMessage = message;
        this.lastErrorKind = 'startup';
      });
    }
  }

  private handleGatewayEvent(event: GatewayEvent): void {
    runInAction(() => {
      if (VERBOSE_ASSISTANT_LOG && event.type !== 'message' && event.type !== 'thinking' && event.type !== 'token_progress') {
        verboseLog('event received', {
          type: event.type,
          runId: (event as { runId?: number }).runId,
          agentId: (event as { agentId?: string }).agentId,
        });
      }
      switch (event.type) {
        case 'message': {
          const key = this.conversations.resolveConversationKey(event.agentId);
          if (!key) break;
          const rt = this.conversations.getOrCreateConversation(key, this.conversations.resolveEventAgentId(event.agentId));
          if (!this.conversations.bindOrValidateRunForRuntime(rt, event.runId)) break;
          this.conversations.appendAssistantDeltaToRuntime(rt, event.delta, event.seq);
          break;
        }
        case 'message_end': {
          const key = this.conversations.resolveConversationKey(event.agentId);
          if (!key) break;
          const rt = this.conversations.getOrCreateConversation(key, this.conversations.resolveEventAgentId(event.agentId));
          if (rt.ignoredRunIds.has(event.runId)) {
            rt.ignoredRunIds.delete(event.runId);
            break;
          }
          if (!this.conversations.bindOrValidateRunForRuntime(rt, event.runId)) break;
          this.conversations.applyChangedFilesToRuntime(rt, event.runId, event.changedFiles ?? []);
          this.conversations.finaliseStreamingMessageForRuntime(rt);
          rt.assistantState = 'idle';
          rt.activeRunId = null;
          rt.hasPendingRun = false;
          if (key === this.conversations.resolveActiveConversationKey()) {
            this.conversations.flushPendingAutoName();
          }
          void this.refreshAgents();
          break;
        }
        case 'status': {
          const eventAgentId = this.conversations.resolveEventAgentId(event.agentId);
          const isForActiveAgent = !eventAgentId || eventAgentId === this.activeAgentId?.trim();

          if (!isForActiveAgent && eventAgentId) {
            const bgRt = this.conversations.conversationsByKey.get(eventAgentId);
            if (bgRt) {
              if (event.state === 'running' && event.activeRunId !== undefined) {
                this.conversations.markRuntimeRunning(bgRt, event.activeRunId);
              } else if (event.state === 'error') {
                bgRt.assistantState = 'error';
                bgRt.activeRunId = null;
                bgRt.hasPendingRun = false;
              } else {
                this.conversations.markRuntimeIdle(bgRt);
              }
            }
            break;
          }

          verboseLog('status transition', {
            state: event.state,
            hasPendingRun: this.conversations.hasPendingRun,
            activeRunId: this.conversations.activeRunId ?? undefined,
            assistantState: this.conversations.assistantState,
          });
          if (event.label) {
            this.statusLabel = event.label;
          }
          const activeKey = this.conversations.resolveActiveConversationKey();
          const activeRt = activeKey ? this.conversations.getOrCreateConversation(activeKey, this.activeAgentId) : undefined;
          const requirements = this.resolveBackendRequirementsForStatusEvent(event.backendRequirements);
          const isChatRunning = this.conversations.shouldTreatAsChatRunning({
            state: event.state,
            activeRunId: event.activeRunId,
            eventAgentId: event.agentId,
          });
          if (isChatRunning) {
            if (activeRt) {
              if (event.activeRunId !== undefined) {
                this.conversations.markRuntimeRunning(activeRt, event.activeRunId);
              } else {
                activeRt.assistantState = 'running';
              }
            }
            if (this.lastErrorKind !== 'run') {
              this.errorMessage = null;
              this.lastErrorKind = null;
            }
          } else if (event.state === 'error') {
            if (activeRt) this.conversations.markRuntimeIdle(activeRt);
            if (activeRt) activeRt.assistantState = 'error';
            this.lastErrorKind = 'startup';
          } else {
            if (activeRt && !activeRt.hasPendingRun) {
              this.conversations.markRuntimeIdle(activeRt);
            }
            if (this.lastErrorKind !== 'run') {
              this.errorMessage = null;
              this.lastErrorKind = null;
            }
          }
          this.isReady = computeGatewayReadiness({
            state: event.state,
            configured: this.configured,
            hasProjectPath: !!this.lastProjectPath,
            requirements,
            isChatRunning,
            hasPendingRun: activeRt?.hasPendingRun ?? false,
          });
          break;
        }
        case 'credential_required': {
          const activeKey2 = this.conversations.resolveActiveConversationKey();
          const activeRt2 = activeKey2 ? this.conversations.conversationsByKey.get(activeKey2) : undefined;
          if (activeRt2) {
            this.conversations.finaliseStreamingMessageForRuntime(activeRt2);
            activeRt2.assistantState = 'idle';
            activeRt2.activeRunId = null;
            activeRt2.hasPendingRun = false;
          }
          this.needsCredential = true;
          this.isReady = false;
          this.statusLabel = `${PRODUCT_NAME} AI • Setup Required`;
          this.lastErrorKind = 'startup';
          break;
        }
        case 'error': {
          const key = this.conversations.resolveConversationKey(event.agentId);
          if (!key) break;
          const rt = this.conversations.conversationsByKey.get(key);
          if (event.runId !== undefined && rt && !this.conversations.bindOrValidateRunForRuntime(rt, event.runId)) {
            break;
          }
          console.error('[gateway] Gateway error event', {
            message: event.message,
            runId: event.runId ?? null,
          });
          if (rt) {
            this.conversations.finaliseStreamingMessageForRuntime(rt);
            rt.assistantState = 'error';
            rt.activeRunId = null;
            rt.hasPendingRun = false;
          }
          const isActiveKey = key === this.conversations.resolveActiveConversationKey();
          if (isActiveKey) {
            this.errorMessage = event.message;
            this.lastErrorKind = event.kind ?? (event.runId !== undefined ? 'run' : 'startup');
          }
          void this.refreshAgents();
          break;
        }
        case 'tool_call': {
          const key = this.conversations.resolveConversationKey(event.agentId);
          if (!key) break;
          const rt = this.conversations.getOrCreateConversation(key, this.conversations.resolveEventAgentId(event.agentId));
          if (!this.conversations.bindOrValidateRunForRuntime(rt, event.runId)) break;
          this.conversations.upsertToolCallToRuntime(rt, event.runId, event);
          break;
        }
        case 'thinking': {
          const key = this.conversations.resolveConversationKey(event.agentId);
          if (!key) break;
          const rt = this.conversations.getOrCreateConversation(key, this.conversations.resolveEventAgentId(event.agentId));
          if (!this.conversations.bindOrValidateRunForRuntime(rt, event.runId)) break;
          this.conversations.appendThinkingDeltaToRuntime(rt, event.runId, event.delta, event.seq);
          break;
        }
        case 'thinking_completed': {
          const key = this.conversations.resolveConversationKey(event.agentId);
          if (!key) break;
          const rt = this.conversations.getOrCreateConversation(key, this.conversations.resolveEventAgentId(event.agentId));
          if (!this.conversations.bindOrValidateRunForRuntime(rt, event.runId)) break;
          this.conversations.applyThinkingCompletedToRuntime(rt, event.runId, event.thinkingDurationMs);
          break;
        }
        case 'token_progress': {
          const key = this.conversations.resolveConversationKey(event.agentId);
          if (!key) break;
          const rt = this.conversations.getOrCreateConversation(key, this.conversations.resolveEventAgentId(event.agentId));
          if (!this.conversations.bindOrValidateRunForRuntime(rt, event.runId)) break;
          this.conversations.applyTokenProgressToRuntime(rt, event.runId, event.tokens);
          break;
        }
        case 'run_status': {
          const key = this.conversations.resolveConversationKey(event.agentId);
          if (!key) break;
          const rt = this.conversations.getOrCreateConversation(key, this.conversations.resolveEventAgentId(event.agentId));
          if (!this.conversations.bindOrValidateRunForRuntime(rt, event.runId)) break;
          this.conversations.appendRunStatusEventToRuntime(rt, event.runId, {
            status: event.status,
            message: event.message,
          });
          break;
        }
        case 'step': {
          const key = this.conversations.resolveConversationKey(event.agentId);
          if (!key) break;
          const rt = this.conversations.getOrCreateConversation(key, this.conversations.resolveEventAgentId(event.agentId));
          if (!this.conversations.bindOrValidateRunForRuntime(rt, event.runId)) break;
          this.conversations.appendStepEventToRuntime(rt, event.runId, {
            status: event.status,
            stepId: event.stepId,
            stepDurationMs: event.stepDurationMs,
            step: event.step,
          });
          break;
        }
        case 'summary': {
          const key = this.conversations.resolveConversationKey(event.agentId);
          if (!key) break;
          const rt = this.conversations.getOrCreateConversation(key, this.conversations.resolveEventAgentId(event.agentId));
          if (!this.conversations.bindOrValidateRunForRuntime(rt, event.runId)) break;
          this.conversations.appendSummaryEventToRuntime(rt, event.runId, {
            status: event.status,
            summary: event.summary,
          });
          break;
        }
        case 'shell_output': {
          const key = this.conversations.resolveConversationKey(event.agentId);
          if (!key) break;
          const rt = this.conversations.getOrCreateConversation(key, this.conversations.resolveEventAgentId(event.agentId));
          if (!this.conversations.bindOrValidateRunForRuntime(rt, event.runId)) break;
          this.conversations.appendShellOutputToRuntime(rt, event.runId, event.event);
          break;
        }
        case 'user_message_appended': {
          const key = this.conversations.resolveConversationKey(event.agentId);
          if (!key) break;
          const rt = this.conversations.getOrCreateConversation(key, this.conversations.resolveEventAgentId(event.agentId));
          if (!this.conversations.bindOrValidateRunForRuntime(rt, event.runId)) break;
          this.conversations.applyUserMessageAppendedToRuntime(rt, event.runId, event.message);
          break;
        }
        case 'usage': {
          const key = this.conversations.resolveConversationKey(event.agentId);
          if (!key) break;
          const rt = this.conversations.getOrCreateConversation(key, this.conversations.resolveEventAgentId(event.agentId));
          if (!this.conversations.bindOrValidateRunForRuntime(rt, event.runId)) break;
          this.conversations.applyUsageToRuntime(rt, event.runId, {
            inputTokens: event.inputTokens,
            outputTokens: event.outputTokens,
            cacheReadTokens: event.cacheReadTokens,
            cacheWriteTokens: event.cacheWriteTokens,
          });
          break;
        }
        case 'approval_request': {
          const key = this.conversations.resolveConversationKey(event.agentId);
          if (!key) break;
          const rt = this.conversations.getOrCreateConversation(key, this.conversations.resolveEventAgentId(event.agentId));
          if (!this.conversations.bindOrValidateRunForRuntime(rt, event.runId)) break;
          this.conversations.appendSystemMessageToRuntime(rt, `Approval required: ${event.requestId}`);
          break;
        }
        case 'mcp_approval_request':
          this.approvals.handleMcpApprovalRequest(event);
          break;
        case 'agent': {
          const previousActiveAgentId = this.activeAgentId;
          const previousDraftTabId = isDraftTabId(this.tabs.activeTabId) ? this.tabs.activeTabId : null;
          const eventAgentId = event.agentId.trim() || null;
          const hadPendingDraftSend = this.conversations.hasPendingDraftLiveRun();
          if (eventAgentId) {
            const draftKey = previousDraftTabId ?? (isDraftTabId(this.tabs.activeTabId) ? this.tabs.activeTabId : null);
            this.conversations.rekeyPendingDraftRuntimeToAgent(draftKey, eventAgentId);
          }
          const shouldAdoptAgent = !this.activeAgentId
            || this.activeAgentId === eventAgentId
            || hadPendingDraftSend
            || (this.isAgentTransitioning && this.agentTransitionTargetAgentId === eventAgentId);
          if (shouldAdoptAgent) {
            this.tabs.adoptAgentTabFromGatewayEvent(eventAgentId, previousActiveAgentId, previousDraftTabId);
            this.conversations.flushPendingAutoName();
          }
          void this.refreshAgents();
          break;
        }
        default:
          break;
      }
    });
  }

  private async syncMetadataFromGatewayInternal(syncProjectPath: string | null): Promise<void> {
    const gateway = window.electronAPI?.gateway;
    if (!gateway) return;

    try {
      const [status, credentialStatus, settings, agents] = await Promise.all([
        gateway.getStatus(),
        gateway.getCredentialStatus(),
        gateway.getSettings(),
        syncProjectPath ? gateway.listAgents(syncProjectPath) : Promise.resolve([]),
      ]);
      if (this.lastProjectPath !== syncProjectPath) {
        return;
      }
      const activeBackendId = normaliseGatewayBackendId(status.activeBackendId);
      const cursorCliStatus = activeBackendId === 'cursor-cli-acp' && gateway.getCursorCliStatus
        ? await gateway.getCursorCliStatus()
        : null;
      runInAction(() => {
        this.applyCredentialStatus(credentialStatus);
        this.cursorCliStatus = cursorCliStatus;
        const normalisedAgents = this.normaliseAgents(agents);
        this.agents = normalisedAgents;
        this.applyStatus({
          ...status,
          backendRequirements: mergeCursorCliRequirement(
            status.backendRequirements,
            cursorCliStatus,
            activeBackendId,
          ),
        });
        this.conversations.syncRuntimeStatesFromAgents(normalisedAgents);
        this.applySettings(settings);
        this.tabs.reconcileAgentTabsAgainstKnownAgents(normalisedAgents);
        this.tabs.reconcileAgentTabs();
        this.tabs.persistTabLayout();
        this.updateOnboardingVisibility();
      });
      this.scheduleModelsRefreshAfterMetadataSync(credentialStatus, cursorCliStatus, activeBackendId);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      runInAction(() => {
        if (this.lastErrorKind !== 'run') {
          this.errorMessage = `Could not sync gateway status. ${message}`;
          this.lastErrorKind = 'startup';
        }
      });
      console.error('[gateway] Failed to sync gateway metadata', { message });
    }
  }

  private scheduleModelsRefreshAfterMetadataSync(
    credentialStatus: { configured?: boolean } | null,
    cursorCliStatus: AssistantCursorCliStatus | null,
    activeBackendId: AssistantGatewayBackendId | null,
  ): void {
    if (!credentialStatus?.configured) {
      return;
    }
    if (activeBackendId === 'cursor-cli-acp' && !cursorCliStatus?.available) {
      return;
    }
    void this.model.loadAvailableModels();
  }

  private normaliseAgents(agents: GatewayAgentInfo[]): GatewayAgentInfo[] {
    const agentsByAgentId = new Map<string, GatewayAgentInfo>();
    for (const agent of agents) {
      const agentId = agent.agentId.trim();
      if (!agentId) {
        continue;
      }

      const createdAt = normaliseAgentTimestamp(agent.createdAt);
      const updatedAt = normaliseAgentTimestamp(agent.updatedAt, createdAt);
      const normalised: GatewayAgentInfo = {
        ...agent,
        agentId,
        displayName: agent.displayName ?? '',
        createdAt,
        updatedAt,
      };

      const existing = agentsByAgentId.get(agentId);
      if (!existing) {
        agentsByAgentId.set(agentId, normalised);
        continue;
      }

      if (normalised.updatedAt > existing.updatedAt) {
        agentsByAgentId.set(agentId, normalised);
        continue;
      }

      if (
        normalised.updatedAt === existing.updatedAt
        && normalised.displayName.trim().length > existing.displayName.trim().length
      ) {
        agentsByAgentId.set(agentId, normalised);
      }
    }

    return [...agentsByAgentId.values()]
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }

  private resolveBackendRequirementsForStatusEvent(
    eventRequirements?: GatewayReadinessRequirement[],
  ): GatewayReadinessRequirement[] | undefined {
    if (eventRequirements) {
      this.lastBackendRequirements = eventRequirements;
      return eventRequirements;
    }
    return mergeCursorCliRequirement(
      this.lastBackendRequirements,
      this.cursorCliStatus,
      this.activeBackendId,
    );
  }

  private applyStatus(status: GatewayStatus): void {
    this.statusLabel = status.statusLabel;
    this.configured = status.configured;
    this.gatewayMode = normaliseGatewayMode(status.mode);
    this.activeBackendId = normaliseGatewayBackendId(status.activeBackendId);
    this.needsCredential = status.needsCredential;
    if (status.backendRequirements) {
      this.lastBackendRequirements = status.backendRequirements;
    }
    const isChatRunning = this.conversations.shouldTreatAsChatRunning({
      state: status.state,
    });
    const hasCliRequirement = hasUnsatisfiedCliRequirement(status.backendRequirements);
    const isOperational = status.configured
      && !!status.projectPath
      && !hasCliRequirement
      && (status.state === 'ready' || isChatRunning);
    const activeKey = this.conversations.resolveActiveConversationKey();
    const activeRt = activeKey ? this.conversations.getOrCreateConversation(activeKey, this.activeAgentId) : undefined;
    if (isChatRunning) {
      if (activeRt) {
        activeRt.assistantState = 'running';
      }
      if (this.lastErrorKind !== 'run') {
        this.lastErrorKind = null;
      }
    } else if (status.state === 'error') {
      if (activeRt) {
        activeRt.assistantState = 'error';
        activeRt.activeRunId = null;
        activeRt.hasPendingRun = false;
      }
      this.lastErrorKind = 'startup';
    } else {
      if (activeRt && !activeRt.hasPendingRun) {
        this.conversations.markRuntimeIdle(activeRt);
      }
      if (this.lastErrorKind !== 'run') {
        this.lastErrorKind = null;
      }
    }
    if (isOperational && this.lastErrorKind !== 'run') {
      this.errorMessage = null;
    }
    this.isReady = computeGatewayReadiness({
      state: status.state,
      configured: status.configured,
      hasProjectPath: !!status.projectPath,
      requirements: status.backendRequirements,
      isChatRunning,
      hasPendingRun: activeRt?.hasPendingRun ?? false,
    });
  }

  private applyCredentialStatus(status: GatewayCredentialStatus): void {
    this.configured = status.configured;
    this.hasDeclinedOnboarding = status.hasDeclinedOnboarding;
    this.credentialFromEnvironment = status.fromEnvironment;
    this.credentialMaskedKey = status.maskedKey;
  }

  private updateOnboardingVisibility(): void {
    if (!this.needsCredential || this.hasDeclinedOnboarding) {
      this.showOnboardingDialog = false;
    }
  }

  private disposeSubscription(): void {
    this.unsubscribe?.();
    this.unsubscribe = null;
  }
}

export const assistantStore = new AssistantStore();
export { normaliseAssistantTranscriptMessages };
